import TableSkeletonElement from "Components/GMS/Products/components/TableSkeletonElement";
import { TTableData, TTableProps } from "types/Components/Common/DeltaTable";
import TableHeaders from "./components/TableHeaders";
import { conditionalStyle } from "./utils";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import useTable from "hooks/useTable";
import Pagination from "../../DesignSystem/Pagination/Pagination";
import LiveDraftButton from "Components/GMS/Products/components/LiveDraftButton";
import {
  DELTA_KEY,
  PLACEHOLDER_IMAGE,
  PRODUCT_NAME_KEY,
} from "Pages/GMS/Products/constants";
import { IndeterminateCheckbox } from "Components/Table/Components/IndeterminateCheckbox";
import TableBodySkeletonElement from "Components/GMS/Products/components/TableBodySkeletonElement";
import Dropdown from "../components/Dropdown";
import { formatDate } from "Common/HelperClass";

/**
 * Table component
 *
 * @param {TTableHeader} headers
 * @param {string} headers.name - label for the table header
 * @param {string} headers.column - map to data key
 * @param {{[x: string]: any}[]} data
 * @param {object} options
 * @param {boolean} selectable - checkbox for rows?
 * @param {boolean} enableColumnDrag - Rearrange columns?
 * @returns
 *
 * ----------------------------------------------------------------
 *
 * Additional header parameters
 *
 * @param {string} className - addtional classname for columns(applied to both header and data)
 * @param {boolean} sortable - is column sortable?
 *
 * ----------------------------------------------------------------
 */

// Parses a CSV-formatted attribute string, respecting double-quoted values.
// e.g. `Mesh,"Mesh,Aluminum","Mesh, Synthetic"` → ["Mesh", "Mesh,Aluminum", "Mesh, Synthetic"]
function parseAttributeCSV(raw: string): string[] {
  // Strict RFC 4180 parser: " only opens quoting at field start.
  // This correctly splits 1",2" into ['1"', '2"'] (inch notation)
  // while still handling "a,b" as a single quoted field ['a,b'].
  const results: string[] = [];
  let current = '';
  let inQuotes = false;
  let fieldStart = true;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (inQuotes) {
      if (ch === '"' && raw[i + 1] === '"') { current += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { current += ch; }
    } else {
      if (ch === '"' && fieldStart) { inQuotes = true; fieldStart = false; }
      else if (ch === ',') { const t = current.trim(); if (t) results.push(t); current = ''; fieldStart = true; }
      else { current += ch; fieldStart = false; }
    }
  }
  const t = current.trim();
  if (t) results.push(t);
  return results;
}

const MAX_TABLE_CHIPS = 2;

const AttributeChipsCell: React.FC<{ values: string[] }> = ({ values }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const visibleValues = values.slice(0, MAX_TABLE_CHIPS);
  const overflowCount = values.length - MAX_TABLE_CHIPS;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    if (popoverOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popoverOpen]);

  return (
    <div className="tw-flex tw-flex-nowrap tw-gap-1 tw-items-center tw-relative" ref={containerRef}>
      {visibleValues.map((val, i) => (
        <span key={i} title={val}
          className="tw-bg-tone-info-tint tw-text-tone-info-ink tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-full tw-max-w-[140px] tw-truncate tw-block">
          {val}
        </span>
      ))}
      {overflowCount > 0 && (
        <span role="button" data-testid="deltatable-index-span-1"
          className="tw-text-xs tw-text-tone-info-ink tw-font-medium tw-cursor-pointer hover:tw-underline tw-whitespace-nowrap"
          onClick={(e) => { e.stopPropagation(); setPopoverOpen((p) => !p); }}
        >
          +{overflowCount} more
        </span>
      )}
      {popoverOpen && (
        <div className="tw-absolute tw-top-full tw-right-0 tw-z-50 tw-bg-surface tw-border tw-border-edge tw-rounded tw-shadow-lg tw-p-3 tw-mt-1 tw-min-w-[180px] tw-max-w-[320px]">
          <p className="tw-text-xs tw-font-semibold tw-text-ink-tertiary tw-mb-2 tw-uppercase tw-tracking-wide">
            All values ({values.length} total)
          </p>
          <div className="tw-flex tw-flex-wrap tw-gap-1">
            {values.map((val, i) => (
              <span key={i} title={val}
                className="tw-bg-tone-info-tint tw-text-tone-info-ink tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-full tw-max-w-[200px] tw-truncate tw-block">
                {val}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DeltaTable = <T extends { [x: string]: any }>({
  columns,
  data,
  options,
  className,
  loading,
  dataLoading,
  pageSize,
  totalRecords,
  selectedAllCondition,
  isSelectedCondition,
  handleSelectAll,
  handleSelect,
  updateFilters,
  dataCallback,
  ...rest
}: TTableProps<T>) => {
  const {
    headers,
    rows,
    filters,
    headerControlOpen,
    defaultHeaders,
    setHeaderControlOpen,
    setHeaders,
    updateLocalFilters,
    handlePageChange,
  } = useTable({ columns, data, options });

  const [tableWidth, setTableWidth] = useState<number | string>("auto");
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);

  useLayoutEffect(() => {
    function handleResize() {
      setTableWidth(tableContainerRef?.current?.clientWidth ?? "auto");
    }
    tableContainerRef?.current && handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    updateFilters?.(filters ?? {});
  }, [filters]);

  useEffect(() => {
    setActiveAccordion(null);
  }, [rows]);

  if (loading) {
    return (
      <div>
        <TableSkeletonElement />
      </div>
    );
  }

  const getData = (item: TTableData<T>) =>
    dataCallback ? dataCallback(item) : item;

  const checkIfRowHasAccordion = (item: T) =>
    options?.accordion?.condition && !!options?.accordion?.condition(item);

  /* Selected rows rest on accent-tint — the § 207 selection ground (it must read against
     the hover/stripe grounds; chips keep tone-info-tint, § 216, so the two never share). */
  const getRowBgColor = (index: number, selected: number) => {
    if (selected === 1) return "tw-bg-accent-tint";
    return index % 2 === 0 || !options?.striped
      ? "tw-bg-surface"
      : "tw-bg-surface-sunken";
  };

  const getStickyToColor = (selected: number) => (index: number) => {
    if (selected === 1) return "tw-to-accent-tint";
    return index % 2 === 0 || !options?.striped
      ? "tw-to-surface"
      : "tw-to-surface-sunken";
  };

  const AccordianComponent = (item: any) =>
    options?.accordion?.component(item) ?? <></>;

  const handleAccordionClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    e.preventDefault();
    setActiveAccordion((prevIndex) => {
      return prevIndex === index ? null : index;
    });
  };

  return (
    <>
      <div
        ref={tableContainerRef}
        className={`table-container tw-border tw-border-edge tw--z-20 ${className}`}
      >
        <div
          className={`tw-overflow-x-auto tw-overflow-y-visible tw-w-full`}
          style={{ height: "auto" }}
        >
          <table
            border={0}
            cellPadding={0}
            className={`tw-w-full tw-text-ink tw-align-top tw-whitespace-nowrap tw-table-auto`}
          >
            <TableHeaders
              handleSave={setHeaders}
              defaultHeaders={defaultHeaders}
              headerControlOpen={headerControlOpen}
              options={options}
              headers={headers}
              setHeaderControlOpen={setHeaderControlOpen}
              rows={rows}
              handleSelectAll={handleSelectAll}
              selectedAllCondition={selectedAllCondition}
            />

            <tbody className={`tw-z-10 tw-relative`}>
              {(dataLoading || !rows) && <TableBodySkeletonElement />}
              {!dataLoading && rows && rows.length === 0 && (
                <tr>
                  <td colSpan={headers.length}>
                    <div
                      style={{ width: tableWidth }}
                      className={`tw-text-xs tw-p-2 tw-text-center tw-sticky tw-left-0 tw-w-full tw-animate-pulse`}
                    >
                      No Data Found.
                    </div>
                  </td>
                </tr>
              )}
              {!dataLoading &&
                rows?.length > 0 &&
                rows.map((deltaItem: TTableData<T>, index: number) => {
                  const item: any = getData(deltaItem);
                  const zIndex = 1000 - index;
                  const selected = isSelectedCondition
                    ? isSelectedCondition(deltaItem)
                    : 0;
                  const backgroundColor = getRowBgColor(index, selected);
                  const dropdownOptions = options?.dropDownCallback
                    ? options?.dropDownCallback(deltaItem)
                    : [];

                  if (!item) return <></>;

                  return (
                    <React.Fragment key={index}>
                      <tr id={`products-table-row-${index}`} className={`tw-h-1 tw-border tw-border-edge`}>
                        {
                          // check box
                          options?.selectable && (
                            <td
                              style={{ zIndex, height: "inhe rit" }}
                              className={`tw-text-ink tw-sticky tw-left-0 tw-z-10 ${backgroundColor}`}
                            >
                              <div
                                style={{
                                  paddingLeft:
                                    options?.firstColPaddingLeft ?? 0,
                                }}
                                className={`tw-w-12 tw-text-center`}
                              >
                                <IndeterminateCheckbox
                                  checked={selected === 1 && "checked"}
                                  indeterminate={selected === 0}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                  ) => handleSelect(e, deltaItem)}
                                />
                              </div>
                            </td>
                          )
                        }
                        {// rows
                        headers
                          ?.filter((header) => header.display)
                          .map((header, key) => {
                            const image =
                              !options?.image?.(item) ||
                              options?.image?.(item) === ""
                                ? PLACEHOLDER_IMAGE
                                : options?.image?.(item);
                            if (header.column === "created_at") {
                              item[header.column] = formatDate(
                                item[header.column]
                              );
                            }
                            return (
                              <td
                                key={key}
                                style={{
                                  ...conditionalStyle(header.options),
                                  height: "inherit",
                                }}
                                className={`tw-text-ink ${header.options?.sticky?.[0] ? `tw-z-10 ${backgroundColor}` : ""}`}
                              >
                                <div
                                  className={`${
                                    header.options?.sticky?.[0]
                                      ? "tw-shadow-r"
                                      : ""
                                  } tw-w-full tw-h-full tw-pl-2 tw-pr-4 tw-py-2.5 tw-flex tw-space-x-2 tw-items-center  ${
                                    header.options?.callbackClassNames &&
                                    header.options.callbackClassNames(item)
                                  } ${backgroundColor} ${
                                    header.className ?? ""
                                  }`}
                                >
                                  {
                                    options?.image && key === 0 && (
                                      <img
                                        className={`tw-w-7 tw-h-7 tw-block tw-rounded-sm`}
                                        src={image}
                                        alt=""
                                      />
                                    )
                                  }
                                  {header.options?.callbackComponent &&
                                    header.options?.callbackComponent(item)}
                                  {/* These two are keyed off the column NAME, so any table with a
                                      column called "name"/"delta_type" picks them up. A caller that
                                      supplies its own callbackComponent has already rendered the
                                      cell, so skip them — otherwise the cell renders twice (global
                                      search showed every product/category name duplicated, because
                                      its "Name/Id" column is keyed "name" AND passes a
                                      callbackComponent). Mirrors the !callbackComponent guard on the
                                      raw-value branch below. */}
                                  {!header.options?.callbackComponent && header.column === DELTA_KEY && (
                                      <LiveDraftButton
                                          item={item}
                                          type="id"
                                          rowIndex={index}
                                      />
                                  )}
                                  {!header.options?.callbackComponent && header.column === PRODUCT_NAME_KEY && (
                                      <LiveDraftButton
                                          item={item}
                                          type="product"
                                          rowIndex={index}
                                      />
                                  )}
                                  {
                                    // if not both
                                    !header.options?.callbackComponent &&
                                      header.column !== DELTA_KEY && header.column !== PRODUCT_NAME_KEY && (() => {
                                        const rawVal = item[header.column];
                                        if (header.options?.isAttributeColumn) {
                                          if (!rawVal) return <span className="tw-leading-7 tw-text-ink-tertiary">-</span>;
                                          const parsed = Array.isArray(rawVal)
                                            ? rawVal as string[]
                                            : parseAttributeCSV(String(rawVal));
                                          return parsed.length > 0
                                            ? <AttributeChipsCell values={parsed} />
                                            : <span className="tw-leading-7 tw-text-ink-tertiary">-</span>;
                                        }
                                        return (
                                          <span className={`tw-leading-7 tw-truncate tw-max-w-full`}>
                                            {rawVal ? `${rawVal}` : "-"}
                                          </span>
                                        );
                                      })()
                                  }
                                </div>
                              </td>
                            );
                          })}
                        {/* dropdown for options */}
                        {!options?.disableRowOptions && (
                          <td
                            style={{ zIndex, height: "inherit" }}
                            className={`tw-text-ink tw-sticky  ${
                              options?.accordion
                                ? "tw-right-[40px]"
                                : "tw-right-0"
                            }`}
                          >
                            {dropdownOptions && dropdownOptions.length > 0 && (
                              <Dropdown bgColor={backgroundColor}>
                                <Dropdown.Toggle>
                                  <span
                                    className={`tw-absolute tw-top-0 tw-right-full tw-h-full tw-bg-gradient-to-r tw-w-[100px] tw-from-transparent tw-pointer-events-none ${getStickyToColor(
                                      selected
                                    )(index)}`}
                                  ></span>
                                  <i
                                    className={`fas fa-ellipsis-v tw-leading-7 tw-text-ink-tertiary`}
                                  />
                                </Dropdown.Toggle>
                                {dropdownOptions.map(
                                  (dropdownItem, dropdownIndex) => (
                                      <Dropdown.Item key={dropdownIndex + dropdownItem.label} handleClick={() => dropdownItem.callbackFunction() } >
                                        {dropdownItem.label}
                                      </Dropdown.Item>
                                  )
                                )}
                              </Dropdown>
                            )}
                          </td>
                        )}
                        {
                          // accordion
                          options?.accordion && (
                            <td
                              style={{ zIndex, height: "inherit" }}
                              className={`tw-text-ink tw-sticky tw-right-0`}
                            >
                              {checkIfRowHasAccordion(item) ? (
                                <button
                                  type="button"
                                  data-testid={`delta-table-row-${index}-expand`}
                                  aria-label="Expand row"
                                  onClick={(e) =>
                                    handleAccordionClick(e, index)
                                  }
                                  className={`tw-relative tw-p-2 tw-w-[40px] tw-min-w-full tw-text-center tw-h-full ${backgroundColor}`}
                                >
                                  <i
                                    className={`fa fa-chevron-down tw-text-ink-tertiary tw-leading-7`}
                                  />
                                </button>
                              ) : (
                                <div
                                  className={`tw-relative  tw-block tw-p-2 tw-w-[40px] tw-min-w-full tw-h-full ${backgroundColor}`}
                                >
                                  <p className={`tw-leading-7`}>&nbsp;</p>
                                </div>
                              )}
                            </td>
                          )
                        }
                      </tr>
                      {activeAccordion === index && (
                        <tr className={`tw-relative`}>
                          <td colSpan={headers.length + 3}>
                            <div
                              className={`tw-sticky tw-left-0 tw-shadow-sm`}
                              style={{ maxWidth: tableWidth }}
                            >
                              {checkIfRowHasAccordion(item) &&
                                AccordianComponent(deltaItem)}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
      {options?.footer && (
        <div className="tw-py-2">
          <div
            className={`tw-flex tw-justify-end tw-items-center tw-space-x-3 tw-flex-wrap`}
          >
            <div className={`tw-flex tw-items-center tw-space-x-3`}>
              <span>Show</span>
              <select
                className={`tw-border tw-border-edge tw-bg-surface tw-text-ink focus:tw-outline-none tw-p-1`}
                style={{ width: "80px" }}
                aria-label="Records on page select"
                data-testid="delta-table-page-size"
                value={
                  options.pagination === "manual"
                    ? filters?.limit ?? 100
                    : pageSize ?? 10
                }
                onChange={(e) => {
                  const newLimit = parseInt(e.target.value);
                  updateFilters &&
                    updateFilters({
                      limit: newLimit,page:1,
                    });
                  updateLocalFilters({
                    limit: newLimit,page:1,
                  });
                }}
              >
                <option value={undefined}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
            {/*
              Was `PaginatorWithApi` (react-paginate): no landmark, no aria-current,
              off-palette hexes inside inline SVGs. Same controlled contract —
              `handlePageChange(page)` from useTable. `totalRecords ?? 0`, not `?? 1`:
              an unknown count should render nothing, not "Showing 1–1 of 1".
              One visible change: a single page now shows the count summary where
              the old one showed nothing — the count still informs.
            */}
            <Pagination
              page={filters?.page ?? 1}
              totalItems={totalRecords ?? 0}
              pageSize={
                options.pagination === "manual"
                  ? filters?.limit ?? 100
                  : pageSize ?? 10
              }
              onPageChange={handlePageChange}
              size="sm"
              data-testid="delta-table-pagination"
              
            />
          </div>
        </div>
      )}
    </>
  );
};

export default DeltaTable;
